import React, { HTMLAttributes } from "react";
import { Button } from "@/components/Button";
import { Money } from "@/helpers/Money";
import { Cadence } from "./types";
import {
  AutorenewOutlined,
  LocalShippingOutlined,
  DeleteOutlined,
  EditOutlined,
  RedoOutlined,
  AddOutlined,
} from "@mui/icons-material";
import { ProductCard, ProductCardProduct } from "../product/ProductCard";
import { productSelect } from "@/store";
import { cn } from "@/helpers/utils";

export type ButtonAction = {
  displayText: string;
  href?: string;
  action?: () => void;
};

export type SubProductCardProps = {
  product: ProductCardProduct;
  price: Money;
  quantity: number;
  shippingInfo?: Cadence;
  subscriptionActions?: ButtonAction[];
  loading?: boolean;
  disabled?: boolean;
  responsive?: boolean;
  isSubBadge?: boolean;
  scentBg?: "white" | "gray";
} & HTMLAttributes<HTMLElement>;

export const ItemCard = ({
  product,
  price,
  shippingInfo,
  quantity,
  subscriptionActions,
  loading,
  disabled,
  responsive,
  scentBg,
  isSubBadge,
  ...attrs
}: SubProductCardProps) => {
  const combinedFrequency = `${
    shippingInfo?.frequency
  } ${shippingInfo?.unit.getUnitForValue(shippingInfo.frequency)}`;

  const buttonIcon = (displayText: string) => {
    const attrs = {
      className: "h-4 w-4",
    };
    switch (displayText) {
      case "swap":
        return <EditOutlined {...attrs} />;
      case "delete":
        return <DeleteOutlined {...attrs} />;
      case "delay":
        return <RedoOutlined {...attrs} />;
      case "skip":
        return <RedoOutlined {...attrs} />;
      case "edit":
        return <EditOutlined {...attrs} />;
      case "add_to_next_box":
        return <AddOutlined {...attrs} />;
      default:
        return null;
    }
  };
  const buttons = subscriptionActions?.map(({ displayText, href, action }) => (
    <Button
      variant="ghost"
      className={cn(
        "h-12 w-full space-x-2 text-left only:col-span-2",
        responsive && "md:h-full"
      )}
      key={displayText}
      data-portal-button={displayText}
      size="sm"
      component={href ? "a" : "button"}
      href={href}
      onClick={() => {
        action?.();
      }}
      loading={loading}
      disabled={disabled}
    >
      {buttonIcon(displayText)}
      <span>{displayText.replace(/_/g, " ")}</span>
    </Button>
  ));
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg bg-primary-50",
        responsive && "sm:flex-row sm:flex-wrap md:flex-nowrap"
      )}
      {...attrs}
    >
      <ProductCard
        className={cn(
          "w-full !bg-transparent",
          scentBg &&
            scentBg === "white" &&
            "[&_.product-card-scent-name]:bg-white",
          responsive && "sm:basis-2/3 md:grow-[2] md:basis-0"
        )}
        productCardValue={{
          ...product,
          size: `${quantity} x ${product.size}`,
        }}
        productTitle={
          <h3
            data-id="product-card-title"
            className={cn("title text-sm", responsive && "lg:text-base")}
          >
            {productSelect.name(product)}
          </h3>
        }
        compact={true}
        hoverable={false}
      />

      <div
        className={cn(
          "flex items-center justify-between space-x-4 px-4 pb-4",
          responsive &&
            "sm:basis-1/3 sm:flex-col sm:items-end sm:justify-center sm:space-x-0 sm:space-y-4 sm:pt-4 md:flex-1 md:flex-row md:items-center md:justify-around md:space-x-4 md:space-y-0"
        )}
      >
        <p className={cn("body flex items-center space-x-1 text-sm")}>
          {shippingInfo ? (
            <>
              <AutorenewOutlined className="h-4 w-4" />
              <span className="sr-only">Ships every</span>
              <span>{combinedFrequency}</span>
            </>
          ) : (
            <>
              <LocalShippingOutlined className="h-4 w-4" />
              <span>One-time</span>
              <span className="sr-only">delivery</span>
            </>
          )}
        </p>
        <p
          className={cn(
            "body flex flex-col text-sm",
            responsive && "lg:text-base"
          )}
        >
          <span className="font-semibold">{price.mul(quantity).format()}</span>
          {quantity > 1 && (
            <span className="text-xs">({price.format()}/ea.)</span>
          )}
        </p>
      </div>
      {subscriptionActions && (
        <div className="sm:basis-full md:flex md:basis-32">
          <hr
            className={cn(
              "divider-x my-0",
              responsive && "md:divider-y md:h-full"
            )}
          />
          <div
            className={cn(
              "flex w-full divide-x divide-y-0 divide-solid divide-primary-200",
              responsive &&
                "md:flex-col md:divide-x-0 md:divide-y md:self-stretch"
            )}
          >
            {buttons}
          </div>
        </div>
      )}
    </div>
  );
};
